import { useState, type ComponentType } from 'react';
import { api } from '../api';
import { ErrorBox, Icon, Loading, PageHead, Panel, toast } from '../components/ui';
import { BizDrill } from '../drills/BizDrill';
import { CalcDrill } from '../drills/CalcDrill';
import { CalibDrill } from '../drills/CalibDrill';
import { ColorDrill } from '../drills/ColorDrill';
import { DataDrill } from '../drills/DataDrill';
import { FigureDrill } from '../drills/FigureDrill';
import { FluencyDrill } from '../drills/FluencyDrill';
import { FocusDrill } from '../drills/FocusDrill';
import { LangDrill } from '../drills/LangDrill';
import { MechDrill } from '../drills/MechDrill';
import { MemoryDrill } from '../drills/MemoryDrill';
import { DRILL_META, DRILL_ORDER, FACTORS, type DrillOutcome, type DrillProps } from '../drills/meta';
import { OpDrill } from '../drills/OpDrill';
import { PerceptDrill } from '../drills/PerceptDrill';
import { RuleDrill } from '../drills/RuleDrill';
import { SeqDrill } from '../drills/SeqDrill';
import { SjtDrill } from '../drills/SjtDrill';
import { SolveDrill } from '../drills/SolveDrill';
import { SpaceDrill } from '../drills/SpaceDrill';
import { newSeed } from '../drills/rng';
import { errorMessage, pct } from '../format';
import { useApi } from '../hooks';
import type { DrillKey, DrillResult, TrainingPlan, TrainingStep } from '../types';

const DRILL_VIEW: Record<DrillKey, ComponentType<DrillProps>> = {
  rule: RuleDrill, calc: CalcDrill, memory: MemoryDrill, seq: SeqDrill, op: OpDrill, calib: CalibDrill, biz: BizDrill,
  lang: LangDrill, data: DataDrill, figure: FigureDrill, space: SpaceDrill, percept: PerceptDrill, sjt: SjtDrill,
  mech: MechDrill, focus: FocusDrill, color: ColorDrill, fluency: FluencyDrill, solve: SolveDrill,
};

type View =
  | { kind: 'plan' }
  | { kind: 'intro'; key: DrillKey; level: number }
  | { kind: 'play'; key: DrillKey; level: number; seed: number }
  | { kind: 'result'; key: DrillKey; result: DrillResult; summary?: string[] };

const TASK_LABEL: Record<string, string> = { review: '지식 복습', resolve: '예측 결과 입력' };

export function stepLabel(s: TrainingStep): string {
  return s.kind === 'drill' ? DRILL_META[s.key as DrillKey].label : TASK_LABEL[s.key];
}

export function stepDetail(s: TrainingStep): string {
  if (s.kind === 'drill') return `Lv ${s.level}${s.done ? ` · 오늘 ${pct(s.accuracy)}` : ` · ${DRILL_META[s.key as DrillKey].ability}`}`;
  if (s.key === 'review') return s.done ? `오늘 ${s.sessions}장 끝` : `${s.count}장 · 약 ${s.minutes}분`;
  return `${s.count}건 마감`;
}

const nextDrill = (plan: TrainingPlan | null, after?: DrillKey): TrainingStep | undefined =>
  plan?.steps.find((s) => s.kind === 'drill' && !s.done && s.key !== after);

export function Train({ only }: { only?: DrillKey }) {
  const { data: plan, error, reload } = useApi(api.trainingToday);
  const [view, setView] = useState<View>({ kind: 'plan' });
  const [saving, setSaving] = useState(false);

  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!plan) return <Loading />;

  const levelOf = (key: DrillKey) => plan.levels?.[key] ?? plan.steps.find((s) => s.key === key)?.level ?? 1;
  const intro = (key: DrillKey, level = levelOf(key)) => setView({ kind: 'intro', key, level });
  const current: View = view.kind === 'plan' && only ? { kind: 'intro', key: only, level: levelOf(only) } : view;

  const finish = async (key: DrillKey, level: number, o: DrillOutcome) => {
    setSaving(true);
    try {
      const { summary, ...outcome } = o;
      const result = await api.trainingSubmit({ drill: key, level, ...outcome });
      setView({ kind: 'result', key, result, summary });
      reload();
    } catch (e) {
      toast(`저장하지 못했습니다: ${errorMessage(e)}`, 'err');
      setView({ kind: 'plan' });
    } finally {
      setSaving(false);
    }
  };

  if (current.kind === 'intro') {
    const m = DRILL_META[current.key];
    return (
      <>
        <PageHead title={m.label} sub={`Lv ${current.level} · ${m.ability}`} />
        <Panel className="drill-intro">
          <ul className="how">{m.how.map((h) => <li key={h}>{h}</li>)}</ul>
          <p className="small muted">{m.why}</p>
          <div className="row-gap">
            <button type="button" className="btn btn-primary big" autoFocus onClick={() => setView({ kind: 'play', key: current.key, level: current.level, seed: newSeed() })}>
              시작
            </button>
            {!only && <button type="button" className="btn btn-ghost" onClick={() => setView({ kind: 'plan' })}>목록으로</button>}
          </div>
        </Panel>
      </>
    );
  }

  if (current.kind === 'play') {
    const Drill = DRILL_VIEW[current.key];
    return (
      <>
        <PageHead title={DRILL_META[current.key].label} sub={`Lv ${current.level}`} />
        <Panel className="drill-panel">
          {saving ? <Loading /> : <Drill key={current.seed} level={current.level} seed={current.seed} onDone={(o) => finish(current.key, current.level, o)} />}
        </Panel>
      </>
    );
  }

  if (current.kind === 'result') {
    const r = current.result;
    const next = only ? undefined : nextDrill(plan, r.drill);
    return (
      <>
        <PageHead title={DRILL_META[r.drill].label} sub="결과" />
        <Panel className="drill-result">
          <div className="result-score">{r.correct} <span>/ {r.total}</span></div>
          <p className="result-pct">정답률 {pct(r.accuracy)}</p>
          {current.summary && <ul className="result-summary">{current.summary.map((s) => <li key={s}>{s}</li>)}</ul>}
          <p className={`level-change ${r.change > 0 ? 'up' : r.change < 0 ? 'down' : ''}`}>
            {r.change > 0 ? `레벨 업! Lv ${r.level} → ${r.next_level}` : r.change < 0 ? `다음 판은 Lv ${r.next_level} 로 (한 단계 쉽게)` : `다음 판도 Lv ${r.next_level}`}
          </p>
          {r.new_best && <p className="small">최고 기록 — Lv {r.best_cleared} 통과</p>}
          <p className="small muted">80% 이상이면 레벨 업, 50% 미만이면 한 단계 내려갑니다. 늘 조금 어려운 문제를 풀게 됩니다.</p>
          <div className="row-gap">
            {next && (
              <button type="button" className="btn btn-primary big" autoFocus onClick={() => intro(next.key as DrillKey)}>
                다음: {DRILL_META[next.key as DrillKey].label} <Icon name="arrow" size={16} />
              </button>
            )}
            <button type="button" className={next ? 'btn' : 'btn btn-primary big'} autoFocus={!next} onClick={() => intro(r.drill, r.next_level)}>한 판 더</button>
            {only ? (
              <a className="btn btn-ghost" href="#/train">훈련 목록</a>
            ) : (
              <button type="button" className="btn btn-ghost" onClick={() => setView({ kind: 'plan' })}>훈련 목록</button>
            )}
          </div>
        </Panel>
      </>
    );
  }

  return <PlanView plan={plan} onStart={intro} />;
}

function PlanView({ plan, onStart }: { plan: TrainingPlan; onStart: (key: DrillKey) => void }) {
  const next = nextDrill(plan);
  const pendingTask = plan.steps.find((s) => s.kind === 'task' && !s.done);
  return (
    <>
      <PageHead
        title="오늘 훈련"
        sub={`${plan.done ? '오늘 몫 완료' : `약 ${plan.minutes_left}분 남음`}${plan.streak > 0 ? ` · ${plan.streak}일 연속` : ''}`}
      />
      <Panel>
        <ol className="train-steps">
          {plan.steps.map((s) => (
            <li key={s.key} className={s.done ? 'done' : ''}>
              {s.kind === 'drill' ? (
                <button type="button" onClick={() => onStart(s.key as DrillKey)}>
                  <StepBody s={s} />
                </button>
              ) : (
                <a href={s.href}><StepBody s={s} /></a>
              )}
            </li>
          ))}
        </ol>
        <div className="row-gap train-cta">
          {next ? (
            <button type="button" className="btn btn-primary big" onClick={() => onStart(next.key as DrillKey)}>
              {plan.steps.some((s) => s.kind === 'drill' && s.done) ? '이어서 하기' : '훈련 시작'} <Icon name="arrow" size={16} />
            </button>
          ) : pendingTask ? (
            <a className="btn btn-primary big" href={pendingTask.href}>{stepLabel(pendingTask)} 하러 가기 <Icon name="arrow" size={16} /></a>
          ) : (
            <p className="train-done">오늘 훈련 끝! 더 하고 싶으면 종목을 누르세요.</p>
          )}
        </div>
      </Panel>
      <Catalog plan={plan} onStart={onStart} />
      <p className="small muted train-note">
        연구 결과, 훈련은 연습한 능력만 확실히 늘립니다. 그래서 여러 종목을 매일 조금씩 돌아가며 합니다.
        레벨은 정답률로 저절로 맞춰지고, 분석 화면에서 종목별로 늘어나는지 봅니다.
      </p>
    </>
  );
}

function StepBody({ s }: { s: TrainingStep }) {
  return (
    <>
      <span className="task-check" aria-hidden="true">{s.done && <Icon name="check" size={14} />}</span>
      <span className="task-body">
        <span className="task-label">{stepLabel(s)}</span>
        <span className="task-detail">{stepDetail(s)}</span>
      </span>
      <Icon name="arrow" size={16} />
    </>
  );
}

/** 모든 종목 — 적성 요인별로 묶고, 오늘 순서와 상관없이 몇 판이든 풀 수 있다 */
function Catalog({ plan, onStart }: { plan: TrainingPlan; onStart: (key: DrillKey) => void }) {
  const apt = plan.aptitude;
  return (
    <Panel title="모든 종목 — 원하는 만큼" className="catalog">
      <p className="small muted">오늘 훈련과 상관없이 아무 종목이나 몇 판이든 풀 수 있습니다. 레벨은 종목마다 따로 오르내립니다.</p>
      <div className="factor-list">
        {FACTORS.map((f) => {
          const keys = DRILL_ORDER.filter((k) => DRILL_META[k].factor === f.key);
          const score = apt?.scores?.[f.key];
          const pctl = apt?.percentiles?.[f.key];
          return (
            <section key={f.key} className="factor">
              <h3>
                {f.label}
                {score != null && <span className={`apt ${pctl != null && pctl < 30 ? 'weak' : ''}`}>검사 {score}{pctl != null ? ` · 백분위 ${pctl}` : ''}</span>}
              </h3>
              <div className="factor-drills">
                {keys.map((k) => (
                  <button key={k} type="button" className="drill-chip" onClick={() => onStart(k)}>
                    <span className="drill-chip-name">{DRILL_META[k].label}</span>
                    <span className="small muted">Lv {plan.levels?.[k] ?? 1}{plan.played_today?.[k] ? ` · 오늘 ${plan.played_today[k]}판` : ''}</span>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <p className="small muted">고용24 직업적성검사 점수를 <a href="#/settings">설정</a>에 넣으면 약한 요인이 오늘 훈련에 더 자주 나옵니다.</p>
    </Panel>
  );
}
